import { Immutable } from "immer";
import React from "react";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type { ProductValuePropBlock as Value } from "@reactivated";

// NOTE: This component reproduces the legacy server-rendered markup from
// `cms/landing_page.html` (the `product_value_prop_block` branch) verbatim —
// every classname is load-bearing for the existing SCSS, so keep them in sync.
// The block renders TWO top-level siblings (desktop tablist + mobile carousel);
// CSS shows/hides each per breakpoint.
//
// The desktop tablist's `--on` state is driven by `activeIndex`, defaulting to
// 0 so SSR and the initial client render agree (no hydration mismatch).
type IProps = Immutable<{
    value: Value;
}>;

type ValueProp = NonNullable<Value["product_value_prop"]>[number];

const imageUrl = (image: ValueProp["icon_image"]): string => image?.url ?? "";

const sectionBackgroundStyle = (
    image: Immutable<Value["image"]>,
): React.CSSProperties | undefined => {
    // Omit the inline style entirely when the image is null (LifestyleModule
    // precedent) rather than emitting a broken `url()`.
    if (!image) {
        return undefined;
    }
    return { backgroundImage: `url(${image.url})` };
};

export const LandingProductValuePropBlock = (
    props: IProps,
): React.JSX.Element => {
    const v = props.value;
    const items = v.product_value_prop ?? [];
    const [activeIndex, setActiveIndex] = React.useState(0);
    // watchCSS gates the carousel to the mobile breakpoint.
    const mobileContainerRef = React.useRef<HTMLDivElement>(null);
    useFlickity(mobileContainerRef, {
        options: { watchCSS: true, prevNextButtons: false },
    });

    return (
        <>
            <div
                className="l-full-width landing-page-value-props landing-page__section"
                style={sectionBackgroundStyle(v.image)}
            >
                <h2 className="landing-page-value-props__title landing-page__title">
                    {v.title}
                </h2>
                <div
                    role="tablist"
                    className="landing-page-value-props__content"
                >
                    <div className="landing-page-value-props__container">
                        {items.map((item, index) => (
                            <button
                                key={index}
                                aria-selected={
                                    index === activeIndex ? "true" : "false"
                                }
                                aria-controls={`value-props-tabpanel-${index + 1}`}
                                id={`value-props-tab-${index + 1}`}
                                className={
                                    index === activeIndex
                                        ? "landing-page-value-props__block landing-page-value-props__block--on"
                                        : "landing-page-value-props__block"
                                }
                                onClick={() => setActiveIndex(index)}
                            >
                                <div className="landing-page-value-props__icon">
                                    <img
                                        src={imageUrl(item.icon_image)}
                                        alt=""
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                                <div className="landing-page-value-props__item-content">
                                    <div className="landing-page-value-props__item-title landing-page__title">
                                        {item.title}
                                    </div>
                                    <div className="landing-page-value-props__item-desc landing-page__desc">
                                        {item.description}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {items.map((item, index) => (
                        <div
                            key={index}
                            role="tabpanel"
                            aria-labelledby={`value-props-tab-${index + 1}`}
                            id={`value-props-tabpanel-${index + 1}`}
                            className={
                                index === activeIndex
                                    ? "landing-page-value-props__image landing-page-value-props__image--on"
                                    : "landing-page-value-props__image"
                            }
                            style={{
                                backgroundImage: `url(${imageUrl(item.product_image)})`,
                            }}
                        ></div>
                    ))}
                </div>
            </div>

            <div className="l-full-width landing-page-value-props-mobile">
                <div className="landing-page-value-props-mobile__title landing-page__title">
                    {v.title}
                </div>
                <div
                    className="landing-page-value-props-mobile__container"
                    ref={mobileContainerRef}
                >
                    {items.map((item, index) => (
                        <div
                            key={index}
                            className="landing-page-value-props-mobile__block-container"
                        >
                            {/* NOTE: the mobile block background is the
                                BLOCK-level image (v.image), NOT item.product_image
                                — matches the legacy template. */}
                            <div
                                className="landing-page-value-props-mobile__block"
                                style={sectionBackgroundStyle(v.image)}
                            >
                                <div className="landing-page-value-props-mobile__image">
                                    <img
                                        className="landing-page-value-props-mobile__image-item responsive-img"
                                        src={imageUrl(item.product_image)}
                                        alt=""
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                                <div className="landing-page-value-props-mobile__item-content">
                                    <div className="landing-page-value-props-mobile__icon">
                                        <img
                                            className="responsive-img"
                                            src={imageUrl(item.icon_image)}
                                            alt=""
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                    <div className="landing-page-value-props-mobile__item-title landing-page__title">
                                        {item.title}
                                    </div>
                                    <div className="landing-page-value-props-mobile__item-desc landing-page__desc">
                                        {item.description}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};
